import { Reducer } from 'redux';
import deepmerge from 'deepmerge';
import { RiskState } from './types';
import { Q, StudentState, StudentActionTypes } from '../types';
import { UserRole } from '../../auth/types';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import { IFinanceDocuments, IRisk } from '../../../types';
import { hasFinDocsRisks, hasNoRisks, isAllRisksCleared } from '../../../utilities/risks';

export const initialState: IRisk = {
  personalInfoRisk: {
    namesAndAliases: [],
    birthAndNationality: [],
    criminalConvictions: [],
    englishTest: [],
    passportDetails: [],
  },
  historyRisk: {
    priorStudyAndTravel: [],
    priorStudyAndRefusals: [],
    travellersAndDependants: [],
  },
  fundingRisk: {
    organisationSponsorship: [],
    scholarshipSponsorship: [],
    educationSponsorship: [],
    familySponsorship: [],
    other: [],
  },
  preCasInterviewRisk: {
    step1: [],
    step2: [],
    step3: [],
    step4: [],
  },
  docsRisk: {
    parentalConsent: [],
    birthCertificate: [],
  },
  financeDocsRisk: {
    organisationSponsorship: [],
    scholarshipSponsorship: [],
    educationSponsorship: [],
    familySponsorship: [],
    birthCertificate: [],
    evidenceOfIncome: [],
  },
  countryMultiplier: 0,
  clearedRisks: {
    personalInfoRisk: {
      namesAndAliases: [],
      birthAndNationality: [],
      criminalConvictions: [],
      englishTest: [],
      passportDetails: [],
    },
    historyRisk: {
      priorStudyAndTravel: [],
      priorStudyAndRefusals: [],
      travellersAndDependants: [],
    },
    fundingRisk: {
      organisationSponsorship: [],
      scholarshipSponsorship: [],
      educationSponsorship: [],
      familySponsorship: [],
      other: [],
    },
    preCasInterviewRisk: {
      step1: [],
      step2: [],
      step3: [],
      step4: [],
    },
    docsRisk: {
      parentalConsent: [],
      birthCertificate: [],
    },
    financeDocsRisk: {
      organisationSponsorship: [],
      scholarshipSponsorship: [],
      educationSponsorship: [],
      familySponsorship: [],
      birthCertificate: [],
      evidenceOfIncome: [],
    },
  },
};

const reducer: Reducer<RiskState> = (state = initialState, action) => {
  switch (action.type) {
    case StudentActionTypes.GET_STUDENT_RECORD_SUCCESS:
      let risks: any = action.payload.student.risks || {};
      const overwriteMerge = (_: [], sourceArray: []) => sourceArray;
      risks = deepmerge(initialState, risks, {
        arrayMerge: overwriteMerge,
      });
      return {
        ...initialState,
        ...risks,
      };
    case StudentActionTypes.SAVE_PERSONAL_INFO_SUCCESS:
    case StudentActionTypes.SAVE_HISTORY_SUCCESS:
    case StudentActionTypes.SAVE_PRE_CAS_INTERVIEW_SUCCESS:
    case StudentActionTypes.SAVE_FINANCE_SUCCESS:
    case StudentActionTypes.UPDATE_RISKS_SUCCESS:
      if (action.payload.risks) {
        return {
          ...initialState,
          ...action.payload.risks,
          clearedRisks: {
            ...initialState.clearedRisks,
            ...action.payload.risks.clearedRisks,
          },
        };
      }
      return state;

    default:
      return state;
  }
};

export function isChecklistRisk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return (
    state.personalInfoRisk?.namesAndAliases?.length > 0 ||
    state.personalInfoRisk?.birthAndNationality?.length > 0 ||
    state.personalInfoRisk?.criminalConvictions?.length > 0 ||
    state.personalInfoRisk?.englishTest?.length > 0 ||
    state.personalInfoRisk?.passportDetails?.length > 0 ||
    state.historyRisk?.priorStudyAndTravel?.length > 0 ||
    state.historyRisk?.priorStudyAndRefusals?.length > 0 ||
    state.historyRisk?.travellersAndDependants?.length > 0 ||
    state.fundingRisk?.educationSponsorship?.some(s => s.risks?.length > 0) ||
    state.fundingRisk?.familySponsorship?.some(s => s.risks?.length > 0) ||
    state.fundingRisk?.organisationSponsorship.some(s => s.risks?.length > 0) ||
    state.fundingRisk?.other?.some(s => s.risks?.length > 0) ||
    state.fundingRisk?.scholarshipSponsorship?.some(s => s.risks.length > 0)
  );
}

// TODO: test it
export function isInactiveChecklistRisk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;

  const PIPRisks = state.personalInfoRisk ?? initialState.personalInfoRisk;
  const HPRisks = state.historyRisk ?? initialState.historyRisk;
  const FRisks = state.fundingRisk ?? initialState.fundingRisk;

  if (state.clearedRisks?.personalInfoRisk) {
    const PIPClearedRisks = state.clearedRisks.personalInfoRisk;
    if (
      (PIPRisks.namesAndAliases?.length > 0 && PIPClearedRisks.namesAndAliases?.length === 0) ||
      (PIPRisks.birthAndNationality?.length > 0 && PIPClearedRisks.birthAndNationality?.length === 0) ||
      (PIPRisks.criminalConvictions?.length > 0 && PIPClearedRisks.criminalConvictions?.length === 0) ||
      (PIPRisks.englishTest?.length > 0 && PIPClearedRisks.englishTest?.length === 0) ||
      (PIPRisks.passportDetails?.length > 0 && PIPClearedRisks.passportDetails?.length === 0)
    ) {
      return false;
    }
  } else {
    return (
      PIPRisks.namesAndAliases?.length === 0 &&
      PIPRisks.birthAndNationality?.length === 0 &&
      PIPRisks.criminalConvictions?.length === 0 &&
      PIPRisks.englishTest?.length === 0 &&
      PIPRisks.passportDetails?.length === 0
    );
  }
  if (state.clearedRisks.historyRisk) {
    const HPClearedRisks = state.clearedRisks.historyRisk;
    if (
      (HPRisks.priorStudyAndTravel?.length > 0 && HPClearedRisks.priorStudyAndTravel?.length === 0) ||
      (HPRisks.priorStudyAndRefusals?.length > 0 && HPClearedRisks.priorStudyAndRefusals?.length === 0) ||
      (HPRisks.travellersAndDependants?.length > 0 && HPClearedRisks.travellersAndDependants?.length === 0)
    ) {
      return false;
    }
  } else {
    return (
      HPRisks.priorStudyAndTravel?.length === 0 &&
      HPRisks.priorStudyAndRefusals?.length === 0 &&
      HPRisks.travellersAndDependants?.length === 0
    );
  }
  if (state.clearedRisks.fundingRisk) {
    const FClearedRisks = state.clearedRisks.fundingRisk;
    if (
      !isAllRisksCleared(null, FRisks.educationSponsorship, FClearedRisks.educationSponsorship) ||
      !isAllRisksCleared(null, FRisks.familySponsorship, FClearedRisks.familySponsorship) ||
      !isAllRisksCleared(null, FRisks.organisationSponsorship, FClearedRisks.organisationSponsorship) ||
      !isAllRisksCleared(null, FRisks.scholarshipSponsorship, FClearedRisks.scholarshipSponsorship) ||
      !isAllRisksCleared(null, FRisks.other, FClearedRisks.other)
    ) {
      return false;
    }
  } else {
    return (
      hasNoRisks(FRisks.educationSponsorship) &&
      hasNoRisks(FRisks.familySponsorship) &&
      hasNoRisks(FRisks.organisationSponsorship) &&
      hasNoRisks(FRisks.scholarshipSponsorship) &&
      hasNoRisks(FRisks.other)
    );
  }
  return true;
}

export function isDocsRisk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.GENERAL_DOCUMENTS_FLAGS)) return false;
  return (
    state.docsRisk?.parentalConsent?.some(s => s.risks?.length > 0) ||
    state.docsRisk?.birthCertificate?.some(s => s.risks?.length > 0)
  );
}

export function isInactiveDocsRisk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.GENERAL_DOCUMENTS_FLAGS)) return false;

  const risks = state.docsRisk;
  if (state.clearedRisks.docsRisk) {
    const clearedRisks = state.clearedRisks.docsRisk;
    if (
      !isAllRisksCleared(null, risks.parentalConsent, clearedRisks.parentalConsent) ||
      !isAllRisksCleared(null, risks.birthCertificate, clearedRisks.birthCertificate)
    ) {
      return false;
    }
  } else {
    return hasNoRisks(risks.parentalConsent) && hasNoRisks(risks.birthCertificate);
  }
  return true;
}

/*
 * Check if Finance Docs section has any risks.
 * Finance Docs section risks includes:
 * - docRisks: finance documents risks
 * - finRisks: finance risks
 *
 * Note: when checking financial documents risks,
 * need to consider the status of the document
 */
export function isFinDocRisk(state: StudentState, role: UserRole): boolean {
  if (!hasPerm(role, P.FINANCE_DOCUMENTS_FLAGS)) return false;

  const finRisks = state.risks.fundingRisk;
  const docRisks = state.risks.financeDocsRisk;
  const docs = state.financeDocuments;
  return (
    hasFinDocsRisks(docs.educationSponsorship, docRisks.educationSponsorship) ||
    hasFinDocsRisks(
      // should include all Family Sponsorship documents types
      getFamilySponsorshipDocs(docs.familySponsorship),
      docRisks.familySponsorship,
    ) ||
    hasFinDocsRisks(
      // should include two Organization Sponsorship documents types : company and government
      { ...docs.companySponsorship, ...docs.governmentSponsorship },
      docRisks.organisationSponsorship,
    ) ||
    hasFinDocsRisks(docs.scholarshipSponsorship, docRisks.scholarshipSponsorship) ||
    hasFinDocsRisks(
      // should include two Birth Certificate documents types : translation and certificate
      {
        ...docs.birthCertificate.translation,
        ...docs.birthCertificate.certificate,
      },
      docRisks.birthCertificate,
    ) ||
    docRisks.evidenceOfIncome?.some(s => s.risks?.length > 0) ||
    finRisks.organisationSponsorship?.some(s => s.risks.length > 0) ||
    finRisks.scholarshipSponsorship?.some(s => s.risks.length > 0) ||
    finRisks.educationSponsorship?.some(s => s.risks.length > 0) ||
    finRisks.familySponsorship?.some(s => s.risks.length > 0) ||
    finRisks.other?.some(s => s.risks.length > 0)
  );
}

/*
 * Check if all Finance Docs section risks was checked-off.
 * Finance Docs section risks includes:
 * - docRisks: finance documents risks
 * - finRisks: finance risks
 *
 * Note: when checking financial documents risks,
 * need to consider the status of the document
 */
export function isInactiveFinDocsRisk(state: StudentState, role: UserRole): boolean {
  if (!hasPerm(role, P.FINANCE_DOCUMENTS_FLAGS)) return false;

  const docRisks = state.risks.financeDocsRisk;
  const finRisks = state.risks.fundingRisk;
  const docs = state.financeDocuments;
  if (state.risks.clearedRisks?.financeDocsRisk) {
    const docClearedRisks = state.risks.clearedRisks.financeDocsRisk;
    const finClearedRisks = state.risks.clearedRisks.fundingRisk;
    if (
      !isAllRisksCleared(
        docs.educationSponsorship,
        docRisks.educationSponsorship,
        docClearedRisks.educationSponsorship,
      ) ||
      !isAllRisksCleared(
        // should include all Family Sponsorship documents types
        getFamilySponsorshipDocs(docs.familySponsorship),
        docRisks.familySponsorship,
        docClearedRisks.familySponsorship,
      ) ||
      !isAllRisksCleared(
        // should include two Organization Sponsorship documents types : company and government
        { ...docs.companySponsorship, ...docs.governmentSponsorship },
        docRisks.organisationSponsorship,
        docClearedRisks.organisationSponsorship,
      ) ||
      !isAllRisksCleared(
        docs.scholarshipSponsorship,
        docRisks.scholarshipSponsorship,
        docClearedRisks.scholarshipSponsorship,
      ) ||
      !isAllRisksCleared(
        // should include two Birth Certificate documents types : translation and certificate
        {
          ...docs.birthCertificate.translation,
          ...docs.birthCertificate.certificate,
        },
        docRisks.birthCertificate,
        docClearedRisks.birthCertificate,
      ) ||
      !isAllRisksCleared(null, docRisks.evidenceOfIncome, docClearedRisks.evidenceOfIncome) ||
      !isAllRisksCleared(null, finRisks.organisationSponsorship, finClearedRisks?.organisationSponsorship ?? []) ||
      !isAllRisksCleared(null, finRisks.scholarshipSponsorship, finClearedRisks?.scholarshipSponsorship ?? []) ||
      !isAllRisksCleared(null, finRisks.educationSponsorship, finClearedRisks?.educationSponsorship ?? []) ||
      !isAllRisksCleared(null, finRisks.familySponsorship, finClearedRisks?.familySponsorship ?? []) ||
      !isAllRisksCleared(null, finRisks.other, finClearedRisks?.other ?? [])
    ) {
      return false;
    }
  } else {
    return (
      hasNoRisks(docRisks.educationSponsorship) &&
      hasNoRisks(docRisks.familySponsorship) &&
      hasNoRisks(docRisks.organisationSponsorship) &&
      hasNoRisks(docRisks.scholarshipSponsorship) &&
      hasNoRisks(docRisks.birthCertificate) &&
      hasNoRisks(docRisks.evidenceOfIncome) &&
      hasNoRisks(finRisks.organisationSponsorship) &&
      hasNoRisks(finRisks.scholarshipSponsorship) &&
      hasNoRisks(finRisks.educationSponsorship) &&
      hasNoRisks(finRisks.familySponsorship) &&
      hasNoRisks(finRisks.other)
    );
  }
  return true;
}

/*
 * Get list of Family Sponsorship documents.
 *
 * Note: Family Sponsorship docs is divided not only by type but also by sponsors.
 * There are several types of documents for each sponsor:
 *  - bankStatementTranslation
 *  - bankStatement
 *  - bankLetter
 *  - parentLetter
 *  - secondaryBankStatement
 *  - verificationBankLetter
 */
function getFamilySponsorshipDocs(docs: IFinanceDocuments['familySponsorship']) {
  let result = {};
  Object.entries(docs).forEach(([key, item]) => {
    const docsByKey = {
      ...item.bankStatementTranslation,
      ...item.bankStatement,
      ...item.bankLetter,
      ...item.parentLetter,
      ...item.secondaryBankStatement,
      ...item.verificationBankLetter,
    };

    Object.entries(docsByKey).forEach(([docKey, doc]) => {
      result = {
        ...result,
        [`${key}${docKey}`]: doc,
      };
    });
  });
  return result;
}

export function isPreCasRisk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.PRE_CAS_INTERVIEW_FLAGS)) return false;
  return (
    state.preCasInterviewRisk?.step1?.length > 0 ||
    state.preCasInterviewRisk?.step2?.length > 0 ||
    state.preCasInterviewRisk?.step3?.length > 0 ||
    state.preCasInterviewRisk?.step4?.length > 0
  );
}

export function isInactivePreCasRisk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.PRE_CAS_INTERVIEW_FLAGS)) return false;

  const risks = state.preCasInterviewRisk ?? initialState.preCasInterviewRisk;
  if (state.clearedRisks?.preCasInterviewRisk) {
    const clearedRisks = state.clearedRisks?.preCasInterviewRisk ?? initialState.preCasInterviewRisk;
    if (
      (risks.step1?.length > 0 && clearedRisks.step1?.length === 0) ||
      (risks.step2?.length > 0 && clearedRisks.step2?.length === 0) ||
      (risks.step3?.length > 0 && clearedRisks.step3?.length === 0) ||
      (risks.step4?.length > 0 && clearedRisks.step4?.length === 0)
    ) {
      return false;
    }
  } else {
    return (
      risks.step1?.length === 0 && risks.step2?.length === 0 && risks.step3?.length === 0 && risks.step4?.length === 0
    );
  }
  return true;
}

export function PI1Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return state.personalInfoRisk?.namesAndAliases?.includes(Q.HAS_OTHER_NAMES);
}

export function PI2Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return (
    state.personalInfoRisk?.birthAndNationality?.includes(Q.DOB) ||
    state.personalInfoRisk?.birthAndNationality?.includes(Q.IS_COUNTRY_OF_NATIONALITY) ||
    state.personalInfoRisk?.birthAndNationality?.includes(Q.APPLIED_FOR_EUSS)
  );
}

export function PI3Risk(_state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return false;
}

export function PI4Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return state.personalInfoRisk?.criminalConvictions?.includes(Q.HAS_CRIMINAL_CONVICTIONS);
}

export function PI5Risk(_state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return false;
}

export function PI6Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return state.personalInfoRisk?.englishTest?.includes(Q.HAS_ENGLISH_TEST);
}

export function PI7Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return (
    state.personalInfoRisk?.passportDetails?.includes(Q.PASSPORT_STATUS) ||
    state.personalInfoRisk?.passportDetails?.includes(Q.PASSPORT_EXPIRY)
  );
}

export function PI8Risk(_state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return false;
}

export function PI9Risk(_state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return false;
}

export function PI10Risk(_state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return false;
}

export function H1Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return (
    state.historyRisk?.priorStudyAndTravel?.includes(Q.LAST_COURSE_YEAR) ||
    state.historyRisk?.priorStudyAndTravel?.includes(Q.HAS_PREVIOUS_UK_STUDY) ||
    state.historyRisk?.priorStudyAndTravel?.includes(Q.LAST_COURSE_LEVEL)
  );
}

export function H2Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return (
    state.historyRisk?.priorStudyAndRefusals.includes(Q.PREVIOUS_VISA_REJECTION) ||
    state.historyRisk?.priorStudyAndRefusals?.includes(Q.PREV_VISA_REFUSAL) ||
    state.historyRisk?.priorStudyAndRefusals?.includes(Q.PREV_NON_UK_VISA_REFUSAL)
  );
}

export function H3Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return (
    state.historyRisk?.travellersAndDependants?.includes(Q.FAMILY_MEMBERS_TRAVELLING) ||
    state.historyRisk?.travellersAndDependants?.includes(Q.SPOUSE_TRAVELLING) ||
    state.historyRisk?.travellersAndDependants?.includes(Q.CHILDREN_TRAVELLING)
  );
}

export function F1Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.CHECKLIST_FLAGS)) return false;
  return (
    state.fundingRisk?.educationSponsorship?.some(s => s.risks?.length > 0) ||
    state.fundingRisk?.familySponsorship?.some(s => s.risks?.length > 0) ||
    state.fundingRisk?.organisationSponsorship?.some(s => s.risks.length > 0) ||
    state.fundingRisk?.other?.some(s => s.risks?.length > 0) ||
    state.fundingRisk?.scholarshipSponsorship?.some(s => s.risks?.length > 0)
  );
}

export function PCI1Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.PRE_CAS_INTERVIEW_FLAGS)) return false;
  return (
    state.preCasInterviewRisk?.step1?.includes(Q.WHY_UK) ||
    state.preCasInterviewRisk?.step1?.includes(Q.SELECTED_COURSE_REASONS) ||
    state.preCasInterviewRisk?.step1?.includes(Q.SELECTED_UNI_REASONS)
  );
}

export function PCI2Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.PRE_CAS_INTERVIEW_FLAGS)) return false;
  return (
    state.preCasInterviewRisk?.step2?.includes(Q.CAMPUS) ||
    state.preCasInterviewRisk?.step2?.includes(Q.CONSIDER_OTHER_UNI) ||
    state.preCasInterviewRisk?.step2?.includes(Q.CONSIDER_OTHER_COURSES) ||
    state.preCasInterviewRisk?.step2?.includes(Q.SELECTED_RESEARCH_METHODS) ||
    state.preCasInterviewRisk?.step2?.includes(Q.COURSE_ALIGN_FUTURE_CAREER)
  );
}

export function PCI3Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.PRE_CAS_INTERVIEW_FLAGS)) return false;
  return (
    state.preCasInterviewRisk?.step3?.includes(Q.MAX_WORK_HOURS_PER_WEEK) ||
    state.preCasInterviewRisk?.step3?.includes(Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING) ||
    state.preCasInterviewRisk?.step3?.includes(Q.COMPLETE_DEGREE_WORK_COUNTRY) ||
    state.preCasInterviewRisk?.step3?.includes(Q.EXPECTED_SALARY)
  );
}

export function PCI4Risk(state: RiskState, role: UserRole): boolean {
  if (!hasPerm(role, P.PRE_CAS_INTERVIEW_FLAGS)) return false;
  return state.preCasInterviewRisk?.step4?.includes(Q.AWARE_OF_UK_VI_INTERVIEW);
}

export { reducer as riskReducer };
